import { ClientEvent, EventType, MatrixEvent, Room, RoomMember, RoomStateEvent } from 'matrix-js-sdk/src';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import { calculatePermissions, WorkspacePermissionsState } from './utils';

const isWorkspacePermissionEvent = (event: MatrixEvent, workspaceId: string) => {
  if (event.getRoomId() !== workspaceId) return false;
  return event.getType() === EventType.RoomPowerLevels || event.getType() === CustomMeedioMatrixEvents.WORKSPACE_ROLES;
};

const useWorkspacePermissions = (workspaceId: string | null) => {
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext({ requireDefinedMatrixClient: false });
  const [workspacePermissions, setWorkspacePermissions] = useState<WorkspacePermissionsState>(() =>
    calculatePermissions(matrixClient, workspaceId)
  );

  const refreshPermissions = useCallback(() => {
    const workspaceRoom = !!workspaceId && matrixClient?.getRoom(workspaceId);
    if (workspaceId && (!matrixClient || !workspaceRoom || !hasReachedSyncReadyState)) {
      setWorkspacePermissions(calculatePermissions(null, workspaceId));
      return;
    }
    setWorkspacePermissions(calculatePermissions(matrixClient, workspaceId));
  }, [matrixClient, workspaceId, hasReachedSyncReadyState]);

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  useEffect(() => {
    if (!matrixClient || !workspaceId || !hasReachedSyncReadyState) return;

    const handleStateEventUpdate = (event: MatrixEvent) => {
      if (isWorkspacePermissionEvent(event, workspaceId)) refreshPermissions();
    };

    // RoomState.setStateEvents updates RoomMember.powerLevel AFTER emitting
    // RoomStateEvent.Events for the power_levels change. Recompute on the
    // post-update RoomStateEvent.Members so state.maySendStateEvent (which
    // reads member.powerLevel) sees the fresh value on the restore step.
    const handleMembersUpdate = (_event: MatrixEvent, _state: unknown, member: RoomMember) => {
      if (member.roomId === workspaceId && member.userId === matrixClient.getUserId()) {
        refreshPermissions();
      }
    };

    const handleRoomEvent = (room: Room) => {
      if (room.roomId === workspaceId) refreshPermissions();
    };

    matrixClient.on(RoomStateEvent.Events, handleStateEventUpdate);
    matrixClient.on(RoomStateEvent.Members, handleMembersUpdate);
    matrixClient.on(ClientEvent.Room, handleRoomEvent);

    return () => {
      matrixClient.off(RoomStateEvent.Events, handleStateEventUpdate);
      matrixClient.off(RoomStateEvent.Members, handleMembersUpdate);
      matrixClient.off(ClientEvent.Room, handleRoomEvent);
    };
  }, [matrixClient, workspaceId, hasReachedSyncReadyState, refreshPermissions]);

  return workspacePermissions;
};

export default useWorkspacePermissions;
